import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';

const API_URL = process.env.REACT_APP_API_URL;

// 環境変数チェック
if (!API_URL) {
  throw new Error('REACT_APP_API_URL が設定されていません。環境変数を確認してください。');
}

/**
 * 認証トークンを取得する関数
 */
const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    
    // IDトークンを優先使用（Lambda認証で推奨）
    const idToken = session.tokens?.idToken?.toString();
    if (idToken) {
      return idToken;
    }
    
    // フォールバックでアクセストークン使用
    const accessToken = session.tokens?.accessToken?.toString();
    if (accessToken) {
      return accessToken;
    }
    
    throw new Error('有効な認証トークンが取得できません');
  } catch (error) {
    console.error('認証トークン取得エラー:', error);
    throw error;
  }
};

/**
 * 現在のユーザー情報を取得する関数
 *
 * getCurrentUser は呼ばずに idToken の payload からユーザー識別子を取得する。
 * fetchAuthSession と fetchUserAttributes を Promise.all で並列化することで
 * 初期表示時の認証往復を短縮する。
 */
export const getCurrentUserInfo = async () => {
  try {
    const [session, attributes] = await Promise.all([
      fetchAuthSession(),
      fetchUserAttributes().catch(() => ({})),
    ]);

    const idToken = session.tokens?.idToken;
    const claims = idToken?.payload || {};

    return {
      userId: claims.sub,
      username: claims['cognito:username'] || claims.email,
      name: attributes.name || claims['cognito:username'] || claims.email,
      email: claims.email,
      groups: claims['cognito:groups'] || [],
      isDesk: (claims['cognito:groups'] || []).includes('Desks'),
      isReporter: (claims['cognito:groups'] || []).includes('Reporters'),
      isAuthenticated: true
    };
  } catch (error) {
    console.error('ユーザー情報取得エラー:', error);
    return {
      userId: null,
      username: null,
      name: null,
      email: null,
      groups: [],
      isDesk: false,
      isReporter: false,
      isAuthenticated: false
    };
  }
};

/**
 * リクエストヘッダーを生成する関数
 */
const getHeaders = async () => {
  const headers = {
    'Content-Type': 'application/json'
  };
  
  try {
    const token = await getAuthToken();
    headers['Authorization'] = `Bearer ${token}`;
  } catch (error) {
    // 認証トークン取得に失敗した場合は匿名アクセス
    console.warn('認証トークン取得失敗 - 匿名アクセス');
  }
  
  return headers;
};

/**
 * APIエラーハンドリング関数
 */
const handleApiError = async (res, operation) => {
  let errorMessage = `${operation}に失敗しました`;
  
  try {
    const errorData = await res.json();
    if (errorData.message) {
      errorMessage = errorData.message;
    }
  } catch (e) {
    // JSONパースに失敗した場合はデフォルトメッセージを使用
  }

  switch (res.status) {
    case 401:
      throw new Error('認証エラー: ログインしてください');
    case 403:
      throw new Error(`権限エラー: ${errorMessage}`);
    case 404:
      throw new Error('データが見つかりません');
    case 400:
      throw new Error('リクエストエラー: 入力内容を確認してください');
    case 500:
      throw new Error('サーバーエラーが発生しました');
    default:
      throw new Error(errorMessage);
  }
};

/**
 * 全件取得
 */
export const loadCalendarEvents = async () => {
  try {
    const headers = await getHeaders();
    
    const res = await fetch(API_URL, { 
      method: 'GET',
      headers
    });
    
    if (!res.ok) {
      await handleApiError(res, 'イベント取得');
    }
    
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('イベント取得エラー:', error);
    return [];
  }
};

/**
 * 新規作成
 */
export const createCalendarEvent = async (event) => {
  try {
    const headers = await getHeaders();
    
    const res = await fetch(API_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify(event),
    });
    
    if (!res.ok) {
      await handleApiError(res, 'イベント新規作成');
    }
    
    return await res.json();
  } catch (error) {
    console.error('イベント新規作成エラー:', error);
    throw error;
  }
};

/**
 * 全体更新
 */
export const updateCalendarEvent = async (id, event) => {
  try {
    const headers = await getHeaders();
    
    const res = await fetch(`${API_URL}/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(event),
    });
    
    if (!res.ok) {
      await handleApiError(res, 'イベント更新');
    }
    
    return await res.json();
  } catch (error) {
    console.error('イベント更新エラー:', error);
    throw error;
  }
};

/**
 * 部分更新
 */
export const patchCalendarEvent = async (id, updates) => {
  try {
    const headers = await getHeaders();
    
    const res = await fetch(`${API_URL}/${id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(updates),
    });
    
    if (!res.ok) {
      await handleApiError(res, 'イベント部分更新');
    }
    
    return await res.json();
  } catch (error) {
    console.error('イベント部分更新エラー:', error);
    throw error;
  }
};

/**
 * 削除
 */
export const deleteCalendarEvent = async (id) => {
  try {
    const headers = await getHeaders();
    
    const res = await fetch(`${API_URL}/${id}`, {
      method: 'DELETE',
      headers,
    });
    
    if (!res.ok) {
      await handleApiError(res, 'イベント削除');
    }
    
    return await res.json();
  } catch (error) {
    console.error('イベント削除エラー:', error);
    throw error;
  }
};

/**
 * 接続テスト（開発環境用）
 */
export const testConnection = async () => {
  if (process.env.NODE_ENV === 'production') {
    console.warn('本番環境では接続テストは実行されません');
    return null;
  }
  
  try {
    const headers = await getHeaders();
    
    const response = await fetch(API_URL, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    console.log('接続テスト成功');
    return data;
  } catch (error) {
    console.error('接続テストエラー:', error);
    throw error;
  }
};